import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataState, ListResponse, LoadingState, ModuleV1Read, UserInfo, UserUpdate, WorkflowSimple } from '@ca/core/models';
import { ModulesPublicServiceV1, UserNotificationService, UserService, WorkflowV1Service } from '@ca/core/services';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, EMPTY, Observable, combineLatest, forkJoin, from, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { PERMISSION_APPLICATIONS, PERMISSION_MYLAB, PERMISSION_MYLAB_MODULES, PERMISSION_MYLAB_WORKFLOWS } from 'src/app/shared/constants/constants';
import { PortraitEditorComponent } from '../../components/portrait-editor/portrait-editor.component';
import { ProfileEditorComponent } from '../../components/profile-editor/profile-editor.component';

@Injectable()
export class UserProfileFacadeService {
    private isMine$$: BehaviorSubject<boolean | null> = new BehaviorSubject(null);
    private userBasicInfoState$$: BehaviorSubject<DataState<UserInfo>> = new BehaviorSubject({ loadingState: LoadingState.INIT });
    private featuredModulesState$$: BehaviorSubject<DataState<ListResponse<ModuleV1Read>>> = new BehaviorSubject({ loadingState: LoadingState.INIT });
    private featuredWorkflowsState$$: BehaviorSubject<DataState<ListResponse<WorkflowSimple>>> = new BehaviorSubject({ loadingState: LoadingState.INIT });

    constructor(
        private userService: UserService,
        private modalService: NgbModal,

        private userNotificationService: UserNotificationService,

        private modulesPublicService: ModulesPublicServiceV1,
        private workflowV1Service: WorkflowV1Service,
    ) {}

    get isMine$(): Observable<boolean | null> {
        return this.isMine$$.asObservable();
    }

    get userBasicInfoState$(): Observable<DataState<UserInfo>> {
        return this.userBasicInfoState$$.asObservable();
    }

    get featuredModulesState$(): Observable<DataState<ListResponse<ModuleV1Read>>> {
        return this.featuredModulesState$$.asObservable();
    }

    get featuredWorkflowsState$(): Observable<DataState<ListResponse<WorkflowSimple>>> {
        return this.featuredWorkflowsState$$.asObservable();
    }

    get isLoggedIn(): boolean {
        return this.userService.isLoggedIn;
    }

    hasModulesPermissions$(): Observable<boolean> {
        return this.hasPermissions$([PERMISSION_MYLAB, PERMISSION_MYLAB_MODULES]);
    }

    hasWorkflowsPermissions$(): Observable<boolean> {
        return this.hasPermissions$([PERMISSION_MYLAB, PERMISSION_MYLAB_WORKFLOWS]);
    }

    hasApplicationsPermissions$(): Observable<boolean> {
        return this.hasPermissions$([PERMISSION_APPLICATIONS]);
    }

    getBasicUserInfo(userId: string): void {
        this.userBasicInfoState$$.next({ loadingState: LoadingState.LOADING });
        this.getUserInfo$(userId)
            .pipe(tap((userInfo: UserInfo) => this.userBasicInfoState$$.next({ loadingState: LoadingState.LOADED, data: userInfo })))
            .subscribe();
    }

    edit(userInfo: UserInfo): void {
        const modalRef = this.modalService.open(ProfileEditorComponent);
        const profileComponent = <ProfileEditorComponent>modalRef.componentInstance;
        profileComponent.user = userInfo;
        from(modalRef.result)
            .pipe(
                switchMap(() => this.userService.refreshUserInfo$()),
                switchMap(() => this.getUserInfo$(userInfo.id, true)),
                tap(userInfoOfProfileToShow => this.userBasicInfoState$$.next({ loadingState: LoadingState.LOADED, data: userInfoOfProfileToShow })),
            )
            .subscribe();
    }

    updateImage(userInfo: UserInfo): void {
        const modalRef = this.modalService.open(PortraitEditorComponent, { size: 'lg' });
        const portraitComponent = <PortraitEditorComponent>modalRef.componentInstance;
        portraitComponent.userInfo = userInfo;
        from(modalRef.result)
            .pipe(
                switchMap(() => this.userService.refreshUserImages$()),
                tap((scopedUserInfo: UserInfo) => {
                    const userBasicInfo = this.userBasicInfoState$$.getValue().data || {};
                    Object.assign(userBasicInfo, scopedUserInfo);
                    this.userBasicInfoState$$.next({ loadingState: LoadingState.LOADED, data: userInfo });
                }),
            )
            .subscribe();
    }

    updateEmailVisibility(userInfo: UserInfo, value: boolean): void {
        const payload: UserUpdate = {
            title: userInfo.title,
            firstName: userInfo.firstName,
            lastName: userInfo.lastName,
            email: userInfo.email,
            organization: userInfo.organization,
            occupation: userInfo.occupation,
            phoneNumber: userInfo.phoneNumber,
            website: userInfo.website,
            country: userInfo.country,
            city: userInfo.city,
            description: userInfo.description,
            headerImage: userInfo.headerImage,
            picture: userInfo.picture,
            showEmail: value,
        };
        this.userNotificationService.showSpinner();
        combineLatest([
            this.userService.updateUserProfile$(payload),
            this.userBasicInfoState$.pipe(
                filter(state => state.loadingState === LoadingState.LOADED),
                map(state => state.data),
                take(1),
            ),
        ])
            .pipe(
                tap(([scopedUserInfo, userInfo]: [UserInfo, UserInfo]) => {
                    Object.assign(userInfo, scopedUserInfo);
                    this.userBasicInfoState$$.next({ loadingState: LoadingState.LOADED, data: userInfo });
                }),
                catchError(() => {
                    this.userNotificationService.hideSpinner();
                    return EMPTY;
                }),
                take(1),
            )
            .subscribe();
    }

    getFeaturedModules(userId: string): void {
        const httpParams = new HttpParams().set('producerId', userId);
        this.featuredModulesState$$.next({ loadingState: LoadingState.LOADING });
        this.modulesPublicService
            .getPublicModules(httpParams)
            .pipe(
                tap(modules => {
                    this.featuredModulesState$$.next({ loadingState: LoadingState.LOADED, data: modules });
                }),
                take(1),
            )
            .subscribe();
    }

    getFeaturedMWorkflows(userId: string): void {
        const httpParams = new HttpParams().set('producerId', userId);
        this.featuredWorkflowsState$$.next({ loadingState: LoadingState.LOADING });
        this.workflowV1Service
            .getPublicOverviewList(httpParams)
            .pipe(
                tap(workflows => {
                    this.featuredWorkflowsState$$.next({ loadingState: LoadingState.LOADED, data: workflows });
                }),
                take(1),
            )
            .subscribe();
    }

    isUserAdmin(): boolean {
        return this.userService.isAdmin;
    }

    private getUserInfo$(userId: string, refresh: boolean = false): Observable<UserInfo> {
        const userProfileToShow$: Observable<UserInfo | null> = this.userService
            .getUserInfos$([userId])
            .pipe(map((res: UserInfo[]): UserInfo | null => (res.length ? res[0] : null)));
        const scopedUser$: Observable<UserInfo> = this.userService.getMyProfileInfo$(userId);
        return forkJoin([userProfileToShow$, scopedUser$]).pipe(
            map(([userInfoOfProfileToShow, scopedUserInfo]: [UserInfo, UserInfo]) => {
                if (userInfoOfProfileToShow != null) {
                    Object.assign(userInfoOfProfileToShow, scopedUserInfo);
                }
                if (!refresh) {
                    this.isMine$$.next(this.userService.isMine(this.userService.myInfo ?? null, userInfoOfProfileToShow));
                }
                return userInfoOfProfileToShow;
            }),
        );
    }

    private hasPermissions$(permissions: string[]): Observable<boolean> {
        const userPermissions$: Observable<string[] | null> = this.userService.permissions ? of(this.userService.permissions) : this.userService.permissions$;
        return userPermissions$.pipe(
            switchMap(() => {
                return of(this.userService.hasPermissions(permissions));
            }),
        );
    }
}
